import { useMemo } from 'react';
import type { LocationChoice, TerritoryResolution } from '@buzzmap/shared';
import { useDiscoveryOptional } from '../contexts/discoveryStore';
// Imported via the barrel on purpose: the Home-page test files mock the
// barrel (vi.mock('../../hooks')) to control `useUserPreferences`, and this
// hook must stay controllable through the same seam (same coupling rule as
// `useIsDesktop` in App.tsx). No cycle: the barrel does not re-export this
// file. Both `effectiveLocation` and the favorites preference are read
// through it, so no separate UserProvider dependency exists here.
import { useUserPreferences } from './index';

export interface GalleryScope {
  /** ALL resolved search-area centers (v2). `coords` is set instead when absent. */
  centers?: Array<{ lat: number; lng: number }>;
  /** Legacy single-center scope (pre-activation HEAD behaviour). */
  coords?: [number, number];
  radiusKm: number;
  /** Top-ranked gallery categories: explicit URL selection, else favorites. */
  categories: string[];
  /** Weekend gallery categories: ONLY the explicit URL selection (legacy: none). */
  weekendCategories?: string[];
  /** Explicit URL dates (v2) — galleries intersect their own window with these. */
  startDate?: string;
  endDate?: string;
}

/** Legacy single-center scope: the GPS point, else the first saved city. */
function firstCenterOf(loc: LocationChoice): [number, number] | undefined {
  if (loc.type === 'gps') return loc.coords;
  const first = loc.type === 'cities' ? loc.cities[0] : undefined;
  return first ? [first.lat, first.lng] : undefined;
}

/** Radius for the legacy single-center scope (national fallback when none). */
function legacyRadiusKmOf(loc: LocationChoice): number {
  return loc.type === 'cities' || loc.type === 'gps' ? loc.radiusKm : 75;
}

/**
 * Spatial + filter scope for the two Home galleries (T012-FE, plan l.64).
 *
 * - Spatial: in v2 the scope is the FULL resolved search area
 *   (`resolution.searchArea.centers`, e.g. all 12 Lombardia centers) — never
 *   only the first city. Legacy keeps the single first-city/gps center so the
 *   compatible window stays HEAD-equivalent.
 * - Filters: in v2 the galleries obey the explicit URL categories/dates (the
 *   descriptor); with no explicit selection the top-ranked gallery keeps its
 *   favorites preference and the weekend gallery stays unconstrained, exactly
 *   as today.
 *
 * Shared by `HomePage` (mobile) and `HomeDesktop` so the two screens cannot
 * drift apart.
 */
export function useGalleryScope(resolution: TerritoryResolution | null): GalleryScope {
  const discovery = useDiscoveryOptional();
  const { effectiveLocation, preferences } = useUserPreferences();

  const v2 = discovery?.mode === 'v2';
  const descriptor = v2 ? discovery?.descriptor ?? null : null;

  const centers = useMemo(() => {
    if (!v2) return undefined;
    const searchArea = resolution?.searchArea?.centers;
    if (searchArea && searchArea.length > 0) return searchArea;
    const cities = resolution?.location.type === 'cities' ? resolution.location.cities : null;
    if (cities && cities.length > 0) return cities.map((c) => ({ lat: c.lat, lng: c.lng }));
    return undefined;
  }, [v2, resolution]);

  const explicitCategories = descriptor?.categoryIds ?? [];
  const favoriteCategories = preferences?.favoriteCategories ?? [];

  return {
    centers,
    coords: centers ? undefined : firstCenterOf(effectiveLocation),
    radiusKm: legacyRadiusKmOf(effectiveLocation),
    categories: explicitCategories.length > 0 ? explicitCategories : favoriteCategories,
    weekendCategories: explicitCategories.length > 0 ? explicitCategories : undefined,
    startDate: descriptor?.filters.startDate,
    endDate: descriptor?.filters.endDate,
  };
}
